import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from '../config/axios';
import { authService } from '../services/authService';
import Logo from '../assets/logo.svg'; // Ensure your logo is in the assets folder

// Add CSS styles for the checkmark animation
const checkmarkStyles = `
  .tick-animation {
    width: 100px;
    height: 100px;
    margin: 0 auto;
  }
  .checkmark {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    display: block;
    stroke-width: 2;
    stroke: #4BB543;
    stroke-miterlimit: 10;
    box-shadow: inset 0px 0px 0px #4BB543;
    animation: fill .4s ease-in-out .4s forwards, scale .3s ease-in-out .9s both;
  }
  .checkmark__circle {
    stroke-dasharray: 166;
    stroke-dashoffset: 166;
    stroke-width: 2;
    stroke-miterlimit: 10;
    stroke: #4BB543;
    fill: none;
    animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
  }
  .checkmark__check {
    transform-origin: 50% 50%;
    stroke-dasharray: 48;
    stroke-dashoffset: 48;
    animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
  }
  @keyframes stroke {
    100% {
      stroke-dashoffset: 0;
    }
  }
  @keyframes scale {
    0%, 100% {
      transform: none;
    }
    50% {
      transform: scale3d(1.1, 1.1, 1);
    }
  }
  @keyframes fill {
    100% {
      box-shadow: inset 0px 0px 0px 30px #4BB54333;
    }
  }
`;

interface PaymentSuccessProps {}

// Define the transaction details interface
interface TransactionDetails {
  amount: string;
  currency: string;
  planName: string;
  interval: string;
  date: string;
  receipt: string;
  transactionId: string;
}

const PaymentSuccessPage: React.FC<PaymentSuccessProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transactionDetails, setTransactionDetails] = useState<TransactionDetails | null>(null);
  const [countdown, setCountdown] = useState(10);
  const [showTickAnimation, setShowTickAnimation] = useState(true);
  
  // Get session_id from URL query parameter
  const queryParams = new URLSearchParams(location.search);
  const sessionId = queryParams.get('session_id');
  
  useEffect(() => {
    // Set up countdown for redirect
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/visit', { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [navigate]);
  
  useEffect(() => {
    // After 2 seconds, hide the tick animation
    const animationTimer = setTimeout(() => {
      setShowTickAnimation(false);
    }, 2000);
    
    return () => clearTimeout(animationTimer);
  }, []);
  
  useEffect(() => {
    const verifyCheckoutSession = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        if (!sessionId) {
          throw new Error('No session ID provided');
        }
        
        const token = authService.getToken();
        
        if (!token) {
          throw new Error('Authentication token not found');
        }
        
        // Verify the checkout session with the backend
        const response = await axios.post(
          '/api/scribing/subscriptions/verify-checkout-session', 
          { session_id: sessionId },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        console.log('Verification response:', response.data);
        
        // Check if the verification was successful
        if (response.data && (response.data.success || response.data.status === 'success')) {
          // Create transaction details - either from details or construct default values
          const mockTransactionDetails: TransactionDetails = {
            amount: '$40.00', // Default amount
            currency: 'USD',
            planName: 'Starter Pack',
            interval: 'month',
            date: new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            }),
            receipt: 'https://receipt.stripe.com/receipt/',
            transactionId: sessionId
          };
          
          // If response has details, use them
          if (response.data.details) {
            const details = response.data.details;
            mockTransactionDetails.amount = details.amount_total ? 
              `$${(details.amount_total / 100).toFixed(2)}` : mockTransactionDetails.amount;
            mockTransactionDetails.currency = details.currency?.toUpperCase() || mockTransactionDetails.currency;
            mockTransactionDetails.planName = details.plan_name || mockTransactionDetails.planName;
            mockTransactionDetails.interval = details.interval || mockTransactionDetails.interval;
            mockTransactionDetails.receipt = details.receipt_url || mockTransactionDetails.receipt;
          }
          
          setTransactionDetails(mockTransactionDetails);
        } else {
          throw new Error(response.data.message || 'Failed to verify payment');
        }
      } catch (err: any) {
        console.error('Error verifying transaction:', err);
        setError('Failed to verify payment. Please contact support.');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (!showTickAnimation) {
      verifyCheckoutSession();
    }
  }, [sessionId, showTickAnimation]);
  
  // Render the tick animation
  if (showTickAnimation) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="mb-6">
          <img src={Logo} alt="Scribedo Logo" className="h-12" />
        </div>
        <div className="tick-animation">
          <svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
            <circle className="checkmark__circle" cx="26" cy="26" r="25" fill="none" />
            <path className="checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
          </svg>
        </div>
        <style dangerouslySetInnerHTML={{ __html: checkmarkStyles }} />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-2xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="mb-6">
              <img src={Logo} alt="Scribedo Logo" className="h-8 mx-auto" />
            </div>
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0078d4] mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying your payment...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <div className="mb-6">
              <img src={Logo} alt="Scribedo Logo" className="h-8 mx-auto" />
            </div>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Verification Failed</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <div className="mt-6">
              <Link
                to="/visit"
                className="px-4 py-2 bg-[#0078d4] text-white rounded hover:bg-[#106ebe] focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-2 inline-flex items-center"
              >
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Go to Visit Page
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Success header */}
            <div className="bg-green-500 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <img src={Logo} alt="Scribedo Logo" className="h-6 bg-white rounded p-0.5" />
                  <h2 className="text-lg font-bold">Payment Successful</h2>
                </div>
                <div className="text-sm bg-white text-green-500 font-bold rounded-full px-3 py-1">
                  Verified
                </div>
              </div>
            </div>
            
            {/* Transaction details */}
            <div className="p-6">
              <div className="mb-6 text-center">
                <h3 className="text-2xl font-bold text-gray-800 mb-1">Thank You!</h3>
                <p className="text-gray-600">Your subscription has been successfully activated</p>
              </div>
              
              {transactionDetails && (
                <div className="border rounded-lg overflow-hidden mb-6">
                  <div className="bg-gray-50 px-4 py-3 border-b">
                    <h4 className="font-medium text-gray-700">Transaction Details</h4>
                  </div>
                  <div className="divide-y">
                    <div className="px-4 py-3 flex justify-between items-center">
                      <span className="text-gray-600">Plan</span>
                      <span className="font-semibold">{transactionDetails.planName}</span>
                    </div>
                    <div className="px-4 py-3 flex justify-between items-center">
                      <span className="text-gray-600">Amount</span>
                      <span className="font-semibold">{transactionDetails.amount}</span>
                    </div>
                    <div className="px-4 py-3 flex justify-between items-center">
                      <span className="text-gray-600">Billing</span>
                      <span className="font-semibold">
                        {`per ${transactionDetails.interval}`}
                      </span>
                    </div>
                    <div className="px-4 py-3 flex justify-between items-center">
                      <span className="text-gray-600">Date</span>
                      <span className="font-semibold">{transactionDetails.date}</span>
                    </div>
                    <div className="px-4 py-3 flex justify-between items-center">
                      <span className="text-gray-600">Transaction ID</span>
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{transactionDetails.transactionId}</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="bg-blue-50 text-blue-700 px-4 py-3 rounded-lg text-sm flex items-center w-full sm:w-auto">
                  <svg className="h-5 w-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>
                    A receipt has been sent to your email
                  </span>
                </div>
                
                <Link
                  to="/visit"
                  className="bg-[#0078d4] text-white px-6 py-2 rounded hover:bg-[#106ebe] focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-2 inline-flex items-center"
                >
                  Go to Visit Page
                  <span className="ml-2 bg-white bg-opacity-20 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {countdown}
                  </span>
                </Link>
              </div>
              
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  Need help? <a href="#" className="text-[#0078d4] font-medium hover:underline">Contact our support team</a>
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage; 